import NavigationLink from '@components/NavigationLink'
import SmallScreenWarning from '@components/SmallScreenWarning'
import WebsitePreview from '@components/WebsitePreview'
import { useConcierge } from '@hooks/useConcierge'
import { useConciergeId } from '@hooks/useConciergeId'
import { getEnvPrefix } from '@lib/config'
import { pagePadding } from '@store/constant'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ScienceIcon from '@mui/icons-material/Science'
import { Box, Link, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'

const SIDE_PANEL_WIDTH = 280

export default function ConciergePreview() {
  const theme = useTheme()
  const conciergeId = useConciergeId()
  const { concierge } = useConcierge()

  const isLive = concierge.is_live
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const showSidePanel = useMediaQuery(theme.breakpoints.up('md'))

  if (isSmallScreen) {
    return (
      <Box
        component='section'
        sx={{
          padding: pagePadding,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative',
        }}
      >
        <SmallScreenWarning />
        <Box sx={{ flexGrow: 1, height: '100%', position: 'relative' }}>
          <WebsitePreview />
        </Box>
      </Box>
    )
  }

  return (
    <Box
      component='section'
      sx={{
        padding: pagePadding,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: 2,
        position: 'relative',
      }}
    >
      <Typography variant='h2' component='h2'>
        {isLive ? 'Test Your Web Wisdom' : 'Preview Your Web Wisdom'}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexGrow: 1,
          justifyContent: 'center',
          gap: 4,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 0,
          }}
        >
          <WebsitePreview />
        </Box>

        {showSidePanel && (
          <SidePanel isLive={!!isLive} conciergeId={conciergeId} />
        )}
      </Box>
    </Box>
  )
}

interface SidePanelProps {
  isLive: boolean
  conciergeId: string
}

function SidePanel({ isLive, conciergeId }: SidePanelProps) {
  return (
    <Box
      sx={{
        width: SIDE_PANEL_WIDTH,
        minWidth: SIDE_PANEL_WIDTH,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: 2,
          borderRadius: 2,
          backgroundColor: 'secondary.light',
          border: '1px solid',
          borderColor: 'secondary.dark',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <InfoOutlinedIcon
            fontSize='small'
            color='secondary'
            sx={{ mt: 0.25 }}
          />
          <Typography variant='body2'>
            {isLive
              ? "Test out your Web Wisdom AI here (it doesn't count towards your usage limit)."
              : 'Your Web Wisdom has been trained on your website. Try asking it a question.'}
          </Typography>
        </Box>
        <Typography variant='body2'>
          The photo and text are{' '}
          <NavigationLink to='/web-concierge/customize'>
            completely customizable
          </NavigationLink>{' '}
          to your brand.
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Link
        href={`https://${getEnvPrefix()}preview.digitalwisdom.ai/c/${conciergeId}`}
        target='_blank'
        rel='noopener noreferrer'
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: 'warning.light',
          border: '1px solid',
          borderColor: 'warning.main',
          color: 'text.primary',
          textDecoration: 'none',
          transition: 'background-color 0.2s',
          '&:hover': {
            backgroundColor: 'warning.main',
          },
        }}
      >
        <ScienceIcon color='warning' fontSize='small' />
        <Box sx={{ flex: 1 }}>
          <Typography variant='caption' fontWeight={700}>
            Experimental
          </Typography>
          <Typography variant='body2'>
            Preview your site with web wisdom installed
          </Typography>
        </Box>
        <OpenInNewIcon fontSize='small' sx={{ color: 'text.secondary' }} />
      </Link>
    </Box>
  )
}
